import { useCompany, useDispatch, useInvestor } from '@/hooks/useStore';
import Dialog, {
  DefaultDialogActions,
  DialogBody,
  DialogTitle,
} from '@/components/Dialog';
import { API } from '@/api';
import { deleteCompanyInvestor as dispatchDeleteCompanyInvestor } from '@/store/slices/companyInvestorsSlice';
import { showSuccessToaster } from '@/utils/toasters';
import { useAppState } from '@/hooks/useAppState';
import { useToggle } from '@/hooks/useToggle';
import { renderProfileName } from '@/utils/strings';
import { classes } from './utils';

export type DeleteCompanyInvestorDialogProps = {
  companyId: number;
  investorId: number;
  onDelete?: (companyId: number, investorId: number) => void;
  onClose: () => void;
};

export default function DeleteCompanyInvestorDialog({
  companyId,
  investorId,
  onDelete,
  onClose,
}: DeleteCompanyInvestorDialogProps) {
  const dispatch = useDispatch();
  const { onApiError } = useAppState();
  const investor = useInvestor(investorId);
  const company = useCompany(companyId);
  const [isDeleting, toggleDeleting] = useToggle(false);

  const handleDelete = async () => {
    try {
      toggleDeleting(true);
      const result = await API.Companies.removeCompanyInvestor(
        companyId,
        investorId
      );
      if (onDelete) onDelete(companyId, investorId);
      dispatch(dispatchDeleteCompanyInvestor(result));
      showSuccessToaster('Investor removed from company');
      toggleDeleting(false);
      onClose();
    } catch (err) {
      toggleDeleting(false);
      onApiError(err, 'Error removing investor', () => {
        handleDelete();
      });
    }
  };

  if (!investor || !company) return null; // TODO: Show an error message

  return (
    <Dialog className={classes.dialog} onClose={onClose}>
      <DialogTitle title="Remove company investor" onClose={onClose} />
      <DialogBody className={classes.body}>
        <p
          className={
            classes.paragraph
          }>{`Are you sure you want to remove ${renderProfileName(investor.profile)} from ${
          company.name
        }?`}</p>
        <p className={classes.paragraph}>
          This will also remove any investments they have in {company.name}.
        </p>
        <p className={classes.boldParagraph}>This action cannot be undone.</p>
      </DialogBody>
      <DefaultDialogActions
        pending={isDeleting}
        confirmLabel="Remove"
        pendingLabel="Removing..."
        onCancel={onClose}
        onConfirm={handleDelete}
      />
    </Dialog>
  );
}
